import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignReportDto } from '@carbonwebmail/contracts'
import { Link, useParams } from 'react-router'
import { Async } from '../../ui/Async.js'
import { count, when } from '../../ui/format.js'
import type { PillTone } from '../../ui/StatusPill.js'
import { StatusPill } from '../../ui/StatusPill.js'

import { CampaignRecipients } from './CampaignRecipients.js'
import { audienceOf, CAMPAIGN_STATUS } from './campaignLabels.js'
import { SendTimeline } from './SendTimeline.js'
import { stillSending, useCampaignReport } from './useCampaignReport.js'

/**
 * What happened to one campaign.
 *
 * Two tables rather than one, because they answer different questions at
 * different times. **Outcomes** is the send log: what the provider said about
 * each attempt, and it only grows. **Queue** is what is left to do, and it
 * drains. Merging them into a single set of figures would produce a screen
 * where the numbers do not add up to anything, because they are counting
 * different things.
 *
 * `unknown` keeps its own colour in both and is never painted as a failure. The
 * provider never answered, so the mail may well have arrived — this is the
 * number the product exists to show rather than hide. (I7)
 *
 * There is no open rate. Opens need a tracking pixel, which this product
 * deliberately does not have, and an empty "0% opened" would be a lie about a
 * measurement nobody took.
 */
export function CampaignReportPage({ client }: { readonly client: ApiClient }) {
  const { campaignId = '' } = useParams()
  const { state, refresh } = useCampaignReport({ client, campaignId })

  return (
    <Async state={state} skeleton={<ReportSkeleton />} onRetry={() => void refresh()}>
      {(report) => (
        <Report
          report={report}
          client={client}
          campaignId={campaignId}
          onRefresh={() => void refresh()}
        />
      )}
    </Async>
  )
}

function Report({
  report,
  client,
  campaignId,
  onRefresh,
}: {
  readonly report: CampaignReportDto
  readonly client: ApiClient
  readonly campaignId: string
  readonly onRefresh: () => void
}) {
  const { campaign, outcomes, queue } = report
  const status = CAMPAIGN_STATUS[campaign.status]

  return (
    <>
      <header className="mb-6">
        <Link className="font-medium text-[13px] text-t3 hover:text-t1" to="/campaigns">
          ← Campaigns
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">{campaign.name}</h1>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>
        <p className="mt-1 text-[13px] text-t2">
          {campaign.subject} · to {audienceOf(campaign)} · from {campaign.fromEmail}
          {campaign.queuedAt === null ? null : ` · sent ${when(campaign.queuedAt)}`}
        </p>
      </header>

      {campaign.queuedAt === null ? (
        <div className="rounded-card border border-line bg-surface p-10 text-center">
          <h2 className="font-bold text-[15px] text-t1">Not sent yet</h2>
          <p className="mx-auto mt-2 max-w-md text-[13px] text-t2 leading-relaxed">
            Nothing has been queued for this campaign, so there is nothing to report yet.
          </p>
          {/*
            The way on, rather than an instruction to go and find one. A draft's
            name opens the composer directly, but search and a bookmarked URL
            both land here — and a dead end is how somebody decides a feature
            does not exist.
          */}
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link
              className="rounded-pill bg-brand px-4 py-2 font-semibold text-[13px] text-brand-contrast"
              to={`/campaigns/${campaign.campaignId}/edit`}
            >
              Write it
            </Link>
            <Link
              className="rounded-pill border border-brand px-4 py-2 font-semibold text-[13px] text-brand hover:bg-brand-soft"
              to={`/campaigns/${campaign.campaignId}/send`}
            >
              Review and send
            </Link>
          </div>
        </div>
      ) : (
        <>
          <Section
            heading="Outcomes"
            note="What the provider said about each attempt. From the send log, which only grows."
            figures={[
              ['Accepted', outcomes.accepted, 'ok'],
              ['Duplicate', outcomes.duplicate, 'neutral'],
              ['Rejected', outcomes.rejected, 'danger'],
              ['Failed', outcomes.failed, 'warn'],
              ['Unknown', outcomes.unknown, 'unknown'],
            ]}
          />

          <Section
            heading="Queue"
            note={
              stillSending(queue)
                ? 'Still going. A large campaign takes hours — refresh when you want a newer number.'
                : 'Nothing left to send. Anything unknown or dead is waiting on a person, not a worker.'
            }
            figures={[
              ['Waiting', queue.queued, 'neutral'],
              ['In flight', queue.claimed, 'neutral'],
              ['Done', queue.done, 'ok'],
              ['Unknown', queue.unknown, 'unknown'],
              ['Dead', queue.dead, 'danger'],
            ]}
          />

          <SendTimeline timeline={report.timeline} />

          <div className="mt-6 rounded-card border border-line bg-surface p-5">
            <h2 className="font-bold text-[15px] text-t1">Unsubscribes</h2>
            <p className="mt-1 text-[13px] text-t2">
              <strong className="font-num font-semibold text-[15px] text-t1 tabular-nums">
                {count(report.unsubscribed)}
              </strong>{' '}
              {report.unsubscribed === 1 ? 'person' : 'people'} left the list from this campaign.
            </p>
            {/*
              Said rather than quietly omitted. A bounce arrives by webhook days
              later carrying the provider's own message id, not a campaign, so
              counting it against one send would be a guess — and a guess on this
              screen is exactly the kind of number people make decisions on.
            */}
            <p className="mt-3 border-line border-t pt-3 text-[12px] text-t3">
              Bounces and complaints are not counted per campaign: they arrive later and identify a
              message rather than a send.{' '}
              <Link className="text-brand" to="/suppressions">
                See the suppression list
              </Link>{' '}
              for every address that is now blocked, and why.
            </p>
          </div>

          <CampaignRecipients client={client} campaignId={campaignId} />

          <button
            type="button"
            onClick={onRefresh}
            className="mt-6 rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:bg-line-2 hover:text-t1"
          >
            Refresh
          </button>
        </>
      )}
    </>
  )
}

function Section({
  heading,
  note,
  figures,
}: {
  readonly heading: string
  readonly note: string
  readonly figures: readonly (readonly [string, number, PillTone])[]
}) {
  return (
    // Named, so it is a landmark a reader can jump between — and so "Unknown"
    // in the figures is distinguishable from "Unknown" elsewhere on the page.
    <section aria-label={heading} className="mt-6 first:mt-0">
      <h2 className="font-bold text-[15px] text-t1">{heading}</h2>
      <p className="mt-1 mb-3 text-[13px] text-t2">{note}</p>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {figures.map(([label, value, tone]) => (
          <div className="rounded-card border border-line bg-surface p-4" key={label}>
            <StatusPill tone={tone}>{label}</StatusPill>
            <p className="mt-2 font-bold font-num text-[22px] text-t1 tabular-nums">
              {count(value)}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function ReportSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {[0, 1, 2, 3, 4].map((slot) => (
        <div
          className="h-[92px] animate-pulse rounded-card border border-line bg-surface"
          key={slot}
        />
      ))}
    </div>
  )
}
